import { EvaChartStates } from "types/factory";

import { DataEnum } from "models";
import { ChartSeries } from "models/model";

import { checkSeriesDataYValueGreaterThanMax } from "./utils/checkSeriesDataYValueGreaterThanMax";
import { convertToNumber } from "./utils/convertToNumber";
import { getYMaxProductLock } from "./utils/getYMaxProductLock";
import { getYMinProductLock } from "./utils/getYMinProductLock";

/**
 * Computes locked Y-axis min/max for a single MPC Y-axis.
 *
 * Priority:
 *  1. DataEnum.Reset  → undefined (user explicitly cleared the lock)
 *  2. User-set lock   → user value
 *  3. Default         → product-based default lock, falling back to series data max
 *
 * @param product     Product label for this axis (e.g. "Oil", "GOR") — used as the mpcLocks key
 * @param axisSeries  Series that belong to this axis (pre-filtered by caller)
 * @param states      Full EvaChartStates passed through from the translator
 */
export function mpcYAxisLocks(
  product: string,
  axisSeries: ChartSeries[],
  states: Partial<EvaChartStates>
): {
  lockedYMin: number | undefined;
  lockedYMax: number | undefined;
  lockedYMinType: DataEnum | undefined;
  lockedYMaxType: DataEnum | undefined;
} {
  // Guard: no series means the product was removed — return safe no-op values
  if (!axisSeries || axisSeries.length === 0) {
    return {
      lockedYMin: undefined,
      lockedYMax: undefined,
      lockedYMinType: undefined,
      lockedYMaxType: undefined
    };
  }

  const axisLock = states.mpcLocks?.y?.[product];
  const minLock = axisLock?.min;
  const maxLock = axisLock?.max;

  const isUserYMinLock =
    minLock?.type !== DataEnum.DefaultProductNumber && minLock?.value != null;
  const isUserYMaxLock =
    maxLock?.type !== DataEnum.DefaultProductNumber && maxLock?.value != null;

  const lockedYMin =
    minLock?.type === DataEnum.Reset
      ? undefined
      : isUserYMinLock
      ? minLock.value
      : getYMinProductLock(product);

  const lockedYMax =
    maxLock?.type === DataEnum.Reset
      ? undefined
      : isUserYMaxLock
      ? maxLock.value
      : checkSeriesDataYValueGreaterThanMax(
          getYMaxProductLock(product),
          axisSeries,
          product
        );

  const lockedYMinType: DataEnum | undefined =
    !isUserYMinLock && lockedYMin !== undefined
      ? DataEnum.DefaultProductNumber
      : undefined;

  const lockedYMaxType: DataEnum | undefined =
    !isUserYMaxLock && lockedYMax !== undefined
      ? DataEnum.DefaultProductNumber
      : undefined;

  return {
    lockedYMin: convertToNumber(lockedYMin),
    lockedYMax: convertToNumber(lockedYMax),
    lockedYMinType,
    lockedYMaxType
  };
}

/**
 * Computes locked X-axis min/max for a single MPC X-axis (e.g. cumulative product axes).
 *
 * Priority:
 *  1. DataEnum.Reset  → undefined (user explicitly cleared the lock)
 *  2. User-set lock   → user value
 *  3. No default      → undefined (cumulative X-axes have no product-specific defaults)
 *
 * @param product  Product label for this axis (e.g. "Oil", "Gas") — used as the mpcLocks key
 * @param states   Full EvaChartStates passed through from the translator
 */
export function mpcXAxisLocks(
  product: string,
  states: Partial<EvaChartStates>
): {
  lockedXMin: number | undefined;
  lockedXMax: number | undefined;
} {
  const axisLock = states.mpcLocks?.x?.[product];
  const minLock = axisLock?.min;
  const maxLock = axisLock?.max;

  const lockedXMin =
    minLock?.type === DataEnum.Reset
      ? undefined
      : minLock?.value !== undefined
      ? convertToNumber(minLock.value)
      : undefined;

  const lockedXMax =
    maxLock?.type === DataEnum.Reset
      ? undefined
      : maxLock?.value !== undefined
      ? convertToNumber(maxLock.value)
      : undefined;

  return { lockedXMin, lockedXMax };
}

/**
 * Computes Y-axis lock values from raw lock entries + product-based defaults.
 * Shared between rateCum/rateDate primary Y-axis and MPC per-product Y-axes.
 */
export function computeYAxisLocks(
  yMinLock: { type?: DataEnum; value?: number } | undefined,
  yMaxLock: { type?: DataEnum; value?: number } | undefined,
  product: string,
  series: ChartSeries[]
): {
  lockedYMin: number | undefined;
  lockedYMax: number | undefined;
  lockedYMinType: DataEnum | undefined;
  lockedYMaxType: DataEnum | undefined;
} {
  const isUserYMinLock =
    yMinLock?.type !== DataEnum.DefaultProductNumber && yMinLock?.value !== undefined;
  const isUserYMaxLock =
    yMaxLock?.type !== DataEnum.DefaultProductNumber && yMaxLock?.value !== undefined;

  const lockedYMin =
    yMinLock?.type === DataEnum.Reset
      ? undefined
      : isUserYMinLock
      ? yMinLock?.value
      : getYMinProductLock(product);

  const lockedYMax =
    yMaxLock?.type === DataEnum.Reset
      ? undefined
      : isUserYMaxLock
      ? yMaxLock?.value
      : checkSeriesDataYValueGreaterThanMax(getYMaxProductLock(product), series, product);

  const lockedYMinType: DataEnum | undefined =
    !isUserYMinLock && lockedYMin ? DataEnum.DefaultProductNumber : undefined;
  const lockedYMaxType: DataEnum | undefined =
    !isUserYMaxLock && lockedYMax ? DataEnum.DefaultProductNumber : undefined;

  return {
    lockedYMin: convertToNumber(lockedYMin),
    lockedYMax: convertToNumber(lockedYMax),
    lockedYMinType,
    lockedYMaxType
  };
}
